import { useId, useState } from 'react';
import { toast } from 'sonner';
import { useSubmit } from '../../hooks/useSubmit';
import { useRequestId } from '../../hooks/useRequestId';
import { voidSale } from '../adminApi';
import { btnDestructive, btnGhost, inputCls } from '../ui/styles';
import { money } from '../../core/format/currency';
import { saleMethods } from '../../core/rules/kassa';
import type { Sale } from '../../core/types/kassa';
type Props = {
  sale: Sale;
  onVoided: () => void;
};

/**
 * Een bon corrigeren, op de dag zelf: de bon telt niet meer mee, de cadeaubon krijgt zijn saldo
 * terug en producten gaan terug op voorraad. Geld terug geef je zelf (pin terugboeken of contant).
 * Met een kuur erop kan het niet: die beëindig je in het dossier.
 */
export function VoidSaleForm({
  sale,
  onVoided
}: Props) {
  const id = useId();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [given, setGiven] = useState(false);
  const {
    submit,
    submitting,
    error
  } = useSubmit(voidSale);
  // Per inhoud: na een weggevallen antwoord met een andere reden niet stil de oude correctie terug.
  const requestId = useRequestId(JSON.stringify([reason.trim(), given]));
  const hasKuur = sale.lines.some(l => l.kind === 'kuur');
  if (hasKuur) {
    return <p className="text-[12px] text-muted" data-id="element-2608" data-mp="vd0vg4z8j8uh">Er zit een kuur op deze bon. Corrigeren gaat via het dossier: Kuur stoppen en terugbetalen.</p>;
  }
  if (!asking) {
    return <button type="button" onClick={() => setAsking(true)} className={btnDestructive} data-id="element-2609" data-mp="154f1ywplt8x4">
        Bon corrigeren
      </button>;
  }
  const back = saleMethods(sale);
  const owed = back.pin + back.contant > 0;
  const canConfirm = Boolean(reason.trim()) && (!owed || given);
  const confirm = async () => {
    if (!canConfirm) return;
    const done = await submit(sale.id, {
      requestId,
      reason: reason.trim(),
      ...(owed ? {
        refundGiven: true
      } : {})
    });
    if (!done) return;
    const parts = [back.pin > 0 ? `${money(back.pin)} via de pinautomaat` : '', back.contant > 0 ? `${money(back.contant)} contant` : ''].filter(Boolean);
    toast.success(`Bon ${sale.number} gecorrigeerd`, {
      description: [parts.length ? `Vastgelegd: ${parts.join(' en ')} teruggegeven.` : '', sale.creditCents ? `${money(sale.creditCents)} staat weer op haar tegoed.` : '', sale.issuedVouchers?.length ? 'De verkochte cadeaubon is op € 0 gezet.' : ''].filter(Boolean).join(' ') || undefined
    });
    onVoided();
  };
  return <div className="space-y-2 rounded-md bg-white p-3" role="group" aria-label="Bon corrigeren" data-id="element-2610" data-mp="160u0jvu7yqcv">
      <label htmlFor={id} className="block text-[13px] text-ink" data-id="element-2611" data-mp="1kp6xav14sd8f">
        Waarom? <span className="text-muted" data-id="element-2612" data-mp="1fryh2zzjmj9t">(komt in het overzicht van de dag)</span>
      </label>
      <input id={id} value={reason} onChange={e => setReason(e.target.value)} maxLength={200} placeholder="Bijv. verkeerd product aangeslagen" className={inputCls} data-id="element-2613" data-mp="1trpylap9qhsk" />
      {owed && <label className="flex items-start gap-2.5 text-[13px] text-ink" data-id="element-2614" data-mp="28cvcr7j0btqb">
          <input type="checkbox" checked={given} onChange={e => setGiven(e.target.checked)} className="mt-0.5 h-4 w-4 accent-clay" data-id="element-2615" data-mp="2dcgcvl9wy84m" />
          <span data-id="element-2616" data-mp="1zbnhyzfp9b0z">
            Ik heb {[back.pin > 0 ? `${money(back.pin)} via de pinautomaat` : '', back.contant > 0 ? `${money(back.contant)} contant uit de la` : ''].filter(Boolean).join(' en ')} teruggegeven
          </span>
        </label>}
      {error && <p role="alert" className="text-[12px] text-clay" data-id="element-2617" data-mp="1udc84open0ir">
          {error}
        </p>}
      <div className="flex flex-wrap gap-2" data-id="element-2618" data-mp="1fopquhg7vyow">
        <button type="button" onClick={() => void confirm()} disabled={!canConfirm || submitting} aria-busy={submitting} className={btnDestructive} data-id="element-2619" data-mp="1fq0vejhhlah2">
          {submitting ? 'Corrigeren…' : 'Ja, bon corrigeren'}
        </button>
        <button type="button" onClick={() => setAsking(false)} disabled={submitting} className={btnGhost} data-id="element-2620" data-mp="16q4dr1tilbvi">
          Laat staan
        </button>
      </div>
    </div>;
}